import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import type { PortalLibraryCategoryRow } from '../../types/portalLibrary';
import {
  createPortalCategory,
  deletePortalCategory,
  listPortalCategories,
  renamePortalCategory,
  reorderPortalCategories,
} from './api';

// Kyck college CATEGORIES (Round 4 §6) — the controlled list behind the chip row
// a client reads first. One picker (used by both publish doors and the tile
// edit) and one management section on Tools → Portal library.
//
// ⚠️ Deleting a category that items use is REFUSED by the server with the count;
// this surface shows that message rather than pre-empting it, so the rule has
// one authority.

export const PORTAL_CATEGORIES_QUERY_KEY = ['portal-library-categories'] as const;

export function usePortalCategories() {
  return useQuery({ queryKey: PORTAL_CATEGORIES_QUERY_KEY, queryFn: listPortalCategories });
}

/** The one category picker. Required for college; the caller shows it only there. */
export function CategorySelect({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (id: string) => void;
  id?: string;
}) {
  const q = usePortalCategories();
  const categories = q.data ?? [];
  if (q.isPending) return <Spinner />;
  if (categories.length === 0) {
    return (
      <p className="text-xs text-slate-600">
        There are no categories yet. Add one under Categories on{' '}
        <a className="underline" href="/tools/portal-library">
          Portal library
        </a>
        .
      </p>
    );
  }
  return (
    <SelectField
      id={id}
      value={value}
      onValueChange={onChange}
      options={[
        { value: '', label: 'Choose a category' },
        ...categories.map((c) => ({ value: c.id, label: c.name })),
      ]}
      aria-label="Category"
    />
  );
}

/** The management section: add, rename, reorder, delete. */
export function CategoriesSection() {
  const queryClient = useQueryClient();
  const q = usePortalCategories();
  const [newName, setNewName] = useState('');
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: PORTAL_CATEGORIES_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
  };

  const create = useMutation({
    mutationFn: () => createPortalCategory(newName.trim()),
    onSuccess: () => {
      setNewName('');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderPortalCategories(ids),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deletePortalCategory(id),
    onSuccess: () => {
      toast.success('Category deleted.');
      invalidate();
    },
    // ⚠️ The server's refusal names how many items use it.
    onError: (err: Error) => toast.error(err.message),
  });

  const rows = q.data ?? [];
  const move = (i: number, by: -1 | 1) => {
    const ids = rows.map((r) => r.id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  return (
    <section className="space-y-3 rounded-md border border-slate-200 p-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">Kyck college categories</h2>
        <p className="text-xs text-slate-600">
          The filters along the top of Kyck college, in this order — the first thing a client
          reads. Every college item has exactly one. Tags stay searchable but are never shown.
        </p>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : (
        <ul className="divide-y divide-slate-100 rounded border border-slate-100">
          {rows.map((row, i) => (
            <CategoryRow
              key={row.id}
              row={row}
              first={i === 0}
              last={i === rows.length - 1}
              busy={reorder.isPending || remove.isPending}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
              onDelete={() => remove.mutate(row.id)}
              onRenamed={invalidate}
            />
          ))}
          {rows.length === 0 && (
            <li className="p-2 text-xs text-slate-500">No categories yet.</li>
          )}
        </ul>
      )}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (newName.trim()) create.mutate();
        }}
      >
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category"
          aria-label="New category name"
          maxLength={60}
          className="h-9 max-w-xs"
        />
        <Button type="submit" size="sm" disabled={!newName.trim() || create.isPending}>
          Add
        </Button>
      </form>
    </section>
  );
}

function CategoryRow({
  row,
  first,
  last,
  busy,
  onUp,
  onDown,
  onDelete,
  onRenamed,
}: {
  row: PortalLibraryCategoryRow;
  first: boolean;
  last: boolean;
  busy: boolean;
  onUp: () => void;
  onDown: () => void;
  onDelete: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(row.name);
  const rename = useMutation({
    mutationFn: () => renamePortalCategory(row.id, name.trim()),
    onSuccess: () => {
      toast.success('Renamed. Items keep their category.');
      onRenamed();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const dirty = name.trim() !== row.name && name.trim().length > 0;
  return (
    <li className="flex items-center gap-2 p-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label={`Rename ${row.name}`}
        maxLength={60}
        className="h-8 max-w-xs"
      />
      {dirty && (
        <Button size="sm" variant="outline" onClick={() => rename.mutate()} disabled={rename.isPending}>
          Save
        </Button>
      )}
      <span className="text-xs text-slate-500">
        {row.entryCount} item{row.entryCount === 1 ? '' : 's'}
      </span>
      <div className="ml-auto flex gap-1">
        <Button size="icon" variant="ghost" aria-label="Move up" disabled={first || busy} onClick={onUp}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" aria-label="Move down" disabled={last || busy} onClick={onDown}>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  );
}
